import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {CommonModule} from "@angular/common";
import {FormsModule} from "@angular/forms";
import {LoadingService} from "../../../../services/LoadingService";
import {TOAST_TYPE} from "../../../../constants/Tpiq.constants";
import {ToastService} from "../../../../services/Toast.service";
import {TaxPlanningService} from "../../../../tpiq-services/tax-planning.service";
import {Session} from "../../../../services/Session";
import {TpiqSelectDropdownComponent} from "../../../../input-search/tpiq-select-dropdown/tpiq-select-dropdown.component";
import {FormatAmountPipe} from "../../../../pipes/format-amount.pipe";
import {ModelBasedNumeral} from "../../../../directives/model-numeral";
import {TpiqCustomDatepicker} from "../../../../directives/tpiq-custom-datepicker";
import {calculateProposedFees} from "../../../../tpiq-services/roi-fee-calculator.util";
declare const _: any;

@Component({
  selector: 'app-add-new-fee',
  standalone: true,
  imports: [CommonModule, FormsModule, TpiqSelectDropdownComponent, FormatAmountPipe, ModelBasedNumeral, TpiqCustomDatepicker],
  templateUrl: './add-new-fee.component.html',
  styleUrl: './add-new-fee.component.scss'
})
export class AddNewFeeComponent implements OnInit, OnDestroy {
  org: string = Session.getOrg();
  user: any = {};
  destroy$: EventEmitter<void> = new EventEmitter<void>();
  @Input() editMode: any;
  @Input() feeLine: any;
  @Input() plan: any;
  @Input() costLines: any;
  @Output() onAddOrEditFee: EventEmitter<any> = new EventEmitter<any>();
  finalY1ImplementationValue: any;
  calculatedY1ImplementationFee: any;
  finalY2ImplementationValue: any;

  implementationFeeTypes: Array<any> = [
    {type: 'Select All', priority: 'medium', high: false, medium: true, low: false},
    {type: 'Complexity', priority: 'medium', high: false, medium: true, low: false},
    {type: 'Urgency', priority: 'medium', high: false, medium: true, low: false},
    {type: 'Risk', priority: 'medium', high: false, medium: true, low: false},
    {type: 'Intangible Benefits', priority: 'medium', high: false, medium: true, low: false}];
  implementationFeeColumns: Array<any> = [
    {name: 'type', title: ''},
    {name: 'high', title: 'High'},
    {name: 'medium', title: 'Medium'},
    {name: 'low', title: 'Low'}];
  feeLineData: any = {};
  associatedWith: Array<any> = [
    {id: 'a627ccde-dd57-1090-cefa-fc965526a557', name: 'Effective Rate'},
    {id: '82c36191-8d6f-39e7-f711-947591551608', name: 'Marginal Rate'},
    {id: '5a314bc1-4534-0426-1db4-d18403485072', name: 'Cap Gain Rate'},
    {id: '23a5e1b2-03cf-6675-2596-0952387d4b46', name: 'C Corp Flat Rate'},
    {id: 'b870c7b6-802f-11eb-8aa0-1282986503dc', name: 'Non-deductible'}];
  feePeriods: Array<any> = [{name: 'One Time'}, {name: 'Monthly'}, {name: 'Yearly'}, {name: 'Custom'}];
  years: Array<any> = [];
  taxPlan: any;

  constructor(
    private toastService: ToastService,
    private loadingService: LoadingService,
    private taxPlanningService: TaxPlanningService
  ) {}

  ngOnInit(): void {
    this.user = Session.getUser();
    this.getPlanData();
  }

  getPlanData() {
    this.loadingService.triggerLoadingEvent(true);
    this.taxPlanningService.getTaxPlanForClient(this.org, this.user.id, this.plan.id, null, null, null, false)
      .subscribe({
        next: (response) => {
          this.loadingService.triggerLoadingEvent(false);
          this.taxPlan = response;
          this.fillYears();
          if (!this.editMode) {
            this.feeLineData = {
              type: 'One Time',
              frequency: 'One Time',
              year: this.taxPlan.taxYear,
              associatedWith: '82c36191-8d6f-39e7-f711-947591551608'
            };
            this.calculateImplementationFee();
          } else {
            if(this.feeLine.frequency !== 'Custom')
            {
              this.feeLine.fromMonth = null;
              this.feeLine.toMonth = null;
            }
            this.feeLineData = this.feeLine;
            if(this.feeLineData.type === 'Third Party'){
               this.feeLineData.thirdPartyFee = Number(this.feeLineData.amount) || 0;
            } else {
              // The third-party fee lives on the plan's separate 'Third Party' cost line,
              // not on the edited fee line — rehydrate it so recalcs keep subtracting it.
              const thirdPartyLine = _.find(this.costLines, {type: 'Third Party'});
              this.feeLineData.thirdPartyFee = thirdPartyLine ? Number(thirdPartyLine.amount) || 0 : 0;
            }
            if (this.feeLine && this.feeLine.implementationCalc) {
              this.feeLine.implementationCalc = JSON.parse(this.feeLine.implementationCalc);
              this.implementationFeeTypes = this.feeLine.implementationCalc;
              this.calculateImplementationFee();
              this.finalY1ImplementationValue = this.feeLine.amount
            }
          }
        },
        error: (error) => {
          this.toastService.pop(TOAST_TYPE.error, 'Failed to get tax plan');
          this.loadingService.triggerLoadingEvent(false);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.unsubscribe();
  }

  setImplementationFeePriority(row, column) {
    row.priority = column.name;
    row[column.name] = true;
    if (column.name === 'high') {
      row.medium = false;
      row.low = false;
    } else if (column.name === 'medium') {
      row.high = false;
      row.low = false;
    } else if (column.name === 'low') {
      row.high = false;
      row.medium = false;
    }

    if (row.type === 'Select All') {
      _.each(this.implementationFeeTypes, (line) => {
        if (row.priority === 'high') {
          line.high = true;
          line.medium = false;
          line.low = false;
        } else if (row.priority === 'medium') {
          line.medium = true;
          line.high = false;
          line.low = false;
        } else if (row.priority === 'low') {
          line.low = true;
          line.high = false;
          line.medium = false;
        }
        line.priority = row.priority;
      });
    } else {
      _.each(this.implementationFeeTypes, (line) => {
        if (line.type === 'Select All') {
          line.priority = '';
          line.high = false;
          line.medium = false;
          line.low = false;
        }
      });
    }
    this.calculateImplementationFee();
  }

  setAssociatedWithForFeeItem($event) {
    this.feeLineData.associatedWith = $event.id;
  }

  setPeriod($event){
    this.feeLineData.frequency = $event.name;
  }

  setYear($event){
    this.feeLineData.year = $event.id;
  }

  fillYears(){
    const startYear: any = _.toNumber(this.taxPlan.taxYear);
    const endYear: any = startYear + 10;
    for (let i = startYear; i < endYear; i++){
      this.years.push({id: i, name: i.toString()});
    }
  }

onDate($event: Date | String, type?: string) {
  if (type === 'start') {
    this.feeLineData.fromMonth = $event;
  } else if (type === 'end') {
    this.feeLineData.toMonth = $event;
  }
}

  setTypeOfFee(type) {
    this.feeLineData.type = type;
    if (type === 'One Time') {
      this.feeLineData.frequency = 'One Time';
    } else if (type === 'Recurring') {
      this.feeLineData.frequency = 'Yearly';
    }
  }



  calculateImplementationFee() {
    if (this.validateImplementationFeeLines()) {
      const {y1Proposed, y2Proposed} = calculateProposedFees({
        year1Savings: this.taxPlan?.year1Savings,
        year2Savings: this.taxPlan?.year2Savings,
        implementationFeeTypes: this.implementationFeeTypes,
        thirdPartyFee: this.feeLineData?.thirdPartyFee
      });
      this.finalY1ImplementationValue = y1Proposed;
      this.calculatedY1ImplementationFee = y1Proposed;
      this.finalY2ImplementationValue = y2Proposed;
    } else {
      this.toastService.pop(TOAST_TYPE.error, 'Please select priority for any one type.');
    }
  }

  validateImplementationFeeLines() {
    let existValue = false;
    _.each(this.implementationFeeTypes, (line) => {
      if (line.priority) {
        existValue = true;
      }
    });
    return existValue;
  }

  saveFee() {
    this.onAddOrEditFee.emit({feeLine: this.feeLineData});
  }

  roundOffTheValue(value) {
    if (value) {
      value = _.round(value);
    }
  }

  onThirdPartyFeeBlur() {
    this.roundOffTheValue(this.feeLineData.thirdPartyFee);
    this.calculateImplementationFee();
  }

}
